import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const STATUS_COLORS = { Completed: "secondary", Cancelled: "destructive", "No Show": "outline", Scheduled: "default" };

export default function Appointments() {
  const { fq, facilities, facilityId, user } = useApp();
  const nav = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const [rows, setRows] = useState([]);
  const [date, setDate] = useState(today);
  const [open, setOpen] = useState(false);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [form, setForm] = useState({ patient_id: "", doctor_id: "", date: today, time: "10:00", visit_type: "Walk In", fee: 500 });

  const load = async () => {
    const params = [fq, date ? `date_str=${date}` : ""].filter(Boolean).join("&");
    const { data } = await api.get(`/appointments${params ? `?${params}` : ""}`);
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fq, date]);
  useEffect(() => {
    api.get("/patients").then(({ data }) => setPatients(data));
    api.get("/users?role=doctor").then(({ data }) => setDoctors(data));
  }, []);

  const save = async () => {
    const target = facilityId === "all" ? facilities[0]?.id : Number(facilityId);
    if (!form.patient_id || !form.doctor_id) return toast.error("Select patient and doctor");
    try {
      const { data } = await api.post("/appointments", {
        ...form, patient_id: Number(form.patient_id), doctor_id: Number(form.doctor_id),
        facility_id: target, fee: Number(form.fee) || 0,
      });
      toast.success(`Booked — token ${data.token}`);
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setStatus = async (id, status) => {
    try { await api.put(`/appointments/${id}/status?status=${encodeURIComponent(status)}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="appointments-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-primary">OPD queue</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Appointments</h1>
        </div>
        <div className="flex gap-2 items-end">
          <div className="space-y-1.5">
            <Label className="text-xs">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              data-testid="appointment-date-filter" className="w-40" />
          </div>
          <Button onClick={() => setOpen(true)} className="gap-2" data-testid="book-appointment-button">
            <Plus className="h-4 w-4" /> Book appointment
          </Button>
        </div>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table data-testid="appointments-table">
            <TableHeader>
              <TableRow>
                <TableHead>Token</TableHead><TableHead>Patient</TableHead><TableHead>Doctor</TableHead>
                <TableHead>Time</TableHead><TableHead>Visit</TableHead><TableHead>Fee</TableHead>
                <TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-sm text-muted-foreground">
                  No appointments for this date.
                </TableCell></TableRow>
              )}
              {rows.map((a) => (
                <TableRow key={a.id} data-testid={`appointment-row-${a.id}`} className="hover:bg-muted/50">
                  <TableCell><Badge variant="outline" className="font-mono">#{a.token}</Badge></TableCell>
                  <TableCell className="font-medium">{a.patient_name}
                    <span className="block text-xs font-mono text-muted-foreground">{a.patient_uhid}</span>
                  </TableCell>
                  <TableCell>{a.doctor_name}</TableCell>
                  <TableCell className="font-mono text-xs">{a.time}</TableCell>
                  <TableCell>{a.visit_type}</TableCell>
                  <TableCell className="tabular-nums">₹{a.fee}</TableCell>
                  <TableCell><Badge variant={STATUS_COLORS[a.status] || "default"}>{a.status}</Badge></TableCell>
                  <TableCell className="text-right space-x-1">
                    {["super_admin", "doctor"].includes(user.role) && (
                      <Button size="sm" className="gap-1.5" data-testid={`open-consultation-${a.id}`}
                        onClick={() => nav(`/consultation/new?patient=${a.patient_id}&appointment=${a.id}`)}>
                        <Stethoscope className="h-3.5 w-3.5" /> Consult
                      </Button>
                    )}
                    <Button size="sm" variant="outline" data-testid={`cancel-appointment-${a.id}`}
                      onClick={() => setStatus(a.id, "Cancelled")}>Cancel</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="appointment-dialog">
          <DialogHeader><DialogTitle className="font-heading">Book appointment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Patient</Label>
              <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                <SelectTrigger data-testid="appointment-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
                <SelectContent>
                  {patients.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.name} · {p.uhid}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Doctor</Label>
              <Select value={form.doctor_id} onValueChange={(v) => setForm({ ...form, doctor_id: v })}>
                <SelectTrigger data-testid="appointment-doctor-select"><SelectValue placeholder="Select doctor" /></SelectTrigger>
                <SelectContent>
                  {doctors.map((d) => <SelectItem key={d.id} value={String(d.id)}>{d.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs">Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Time</Label>
                <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Visit type</Label>
                <Select value={form.visit_type} onValueChange={(v) => setForm({ ...form, visit_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Walk In", "Follow Up", "Emergency"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label className="text-xs">Consultation fee</Label>
                <Input type="number" value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-appointment-button">Book & print token</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
